interface LocalForageDbMethodsCore {
  getItem<T>(key: string, callback?: (err: any, value: T | null) => void): Promise<T | null>;
  setItem<T>(key: string, value: T, callback?: (err: any, value: T) => void): Promise<T>;
  removeItem(key: string, callback?: (err: any) => void): Promise<void>;
  clear(callback?: (err: any) => void): Promise<void>;
  length(callback?: (err: any, numberOfKeys: number) => void): Promise<number>;
  key(keyIndex: number, callback?: (err: any, key: string) => void): Promise<string>;
  keys(callback?: (err: any, keys: string[]) => void): Promise<string[]>;
  iterate<T, U>(
    iteratee: (value: T, key: string, iterationNumber: number) => U,
    callback?: (err: any, result: U) => void
  ): Promise<U>;
}

interface LocalForageDbInstanceOptions {
  name?: string;
  storeName?: string;
}

interface LocalForageDropInstanceFn {
  (dbInstanceOptions?: LocalForageDbInstanceOptions, callback?: (err: any) => void): Promise<void>;
}

interface LocalForageDriverMethodsOptional {
  dropInstance?: LocalForageDropInstanceFn;
}

interface LocalForageDriverDbMethods
  extends LocalForageDbMethodsCore,
    LocalForageDriverMethodsOptional {}

interface LocalForageDriver extends LocalForageDriverDbMethods {
  _driver: string;
  _initStorage(options: LocalForageOptions): void;
  _support?: boolean | LocalForageDriverSupportFunc;
}

interface LocalVariableDriver extends LocalForageDriver {
  _storage: object;
}

class BreakIteration extends Error {
  constructor() {
    super('Iteration stopped by BreakIteration');
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// Define the custom driver
export const localVariableDriver: LocalVariableDriver = {
  _driver: 'localVariableStorage',
  _initStorage(options: LocalForageOptions): void {
    // Custom initialization code here...
  },
  _storage: {}, // Local variable to store the values

  clear(callback?: (err: any) => void): Promise<void> {
    // @ts-ignore
    this._storage = {}; // Clear the local variable
    if (callback) {
      callback(null);
    }
    return Promise.resolve();
  },

  getItem<T>(key: string, callback?: (err: any, value: T | null) => void): Promise<T | null> {
    // @ts-ignore
    const value = this._storage[key];
    if (callback) {
      callback(null, value);
    }
    return Promise.resolve(value);
  },

  iterate<T, U>(
    iteratee: (value: T, key: string, iterationNumber: number) => U,
    callback?: (err: any, result: U) => void
  ): Promise<U> {
    let iterationNumber = 0;

    return new Promise<U>((resolve, reject) => {
      this.keys()
        .then((keys) => {
          const promises = keys.map((key) => {
            // @ts-ignore
            const value = this._storage[key];
            return Promise.resolve(iteratee(value, key, iterationNumber++));
          });

          Promise.all(promises)
            .then((iterateeResults) => {
              const lastResult = iterateeResults[iterateeResults.length - 1];
              resolve(lastResult);
              if (callback && lastResult) {
                callback(null, lastResult);
              }
            })
            .catch(reject);
        })
        .catch(reject);
    });
  },

  key(n: number, callback?: (err: any, key: string) => void): Promise<string> {
    const keys = Object.keys(this._storage);
    const key = keys[n] || '';
    if (callback) {
      callback(null, key);
    }
    return Promise.resolve(key);
  },

  keys(callback?: (err: any, keys: string[]) => void): Promise<string[]> {
    const keys = Object.keys(this._storage);
    if (callback) {
      callback(null, keys);
    }
    return Promise.resolve(keys);
  },

  length(callback?: (err: any, numberOfKeys: number) => void): Promise<number> {
    const length = Object.keys(this._storage).length;
    if (callback) {
      callback(null, length);
    }
    return Promise.resolve(length);
  },

  removeItem(key: string, callback?: (err: any) => void): Promise<void> {
    // @ts-ignore
    delete this._storage[key];
    if (callback) {
      callback(null);
    }
    return Promise.resolve();
  },

  setItem<T>(key: string, value: T, callback?: (err: any, value: T) => void): Promise<T> {
    // @ts-ignore
    this._storage[key] = value;
    if (callback) {
      callback(null, value);
    }
    return Promise.resolve(value);
  },
};
